import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, RouteComponentProps } from 'react-router-dom'
import { ProgramPitch } from 'src/components/affiliate/ApplyForm'
import { errorMessage } from 'src/components/affiliate/labels'
import {
    Card,
    CardTitle,
    Checkbox,
    ErrorText,
    InlineSpinner,
    Muted,
    Narrow,
    Page,
    PageTitle,
    ActionButton,
} from 'src/components/affiliate/ui'
import { QueryKeys } from 'src/constants/QueryKeys'
import {
    useAcceptInvite,
    useAffiliateMe,
    useInvitePreview,
    usePublicProgram,
} from 'src/hooks/useAffiliate'
import { getReason, getStatus } from 'src/utils/apiReason'
import { formatDay } from 'src/utils/affiliateFormat'

/** /affiliate/invite/:token (login required): show the offered rates and terms, then join as an active affiliate. */
const AffiliateInvitePage = ({
    match,
    history,
}: RouteComponentProps<{ token: string }>) => {
    const { token } = match.params
    const queryClient = useQueryClient()
    const invite = useInvitePreview(token)
    const program = usePublicProgram()
    const me = useAffiliateMe()
    const accept = useAcceptInvite(token)
    const [checked, setChecked] = useState(false)

    const submit = () => {
        if (!program.data) return
        accept.mutate(program.data.termsVersion, {
            onSuccess: () => history.push('/affiliate'),
            onError: (error) => {
                if (getReason(error) === 'terms_outdated') {
                    setChecked(false)
                    queryClient.invalidateQueries({
                        queryKey: [QueryKeys.affiliateProgramPublic],
                    })
                }
            },
        })
    }

    let body
    if (invite.isPending || program.isPending || me.isPending) {
        body = <InlineSpinner />
    } else if (invite.isError) {
        // A malformed token fails validation (400) rather than returning invite_invalid.
        const message =
            getStatus(invite.error) === 400
                ? 'This invite link is not valid.'
                : errorMessage(invite.error)
        body = (
            <Card>
                <CardTitle>Invite unavailable</CardTitle>
                <Muted>{message}</Muted>
            </Card>
        )
    } else if (me.data) {
        // The API turns away anyone with an affiliate record, including rejected applicants.
        body = (
            <Card>
                <CardTitle>You already have an affiliate account</CardTitle>
                <Muted>
                    <Link to="/affiliate" style={{ color: '#8fa0ff' }}>
                        Go to your Affiliate tab
                    </Link>
                </Muted>
            </Card>
        )
    } else if (invite.data && program.data) {
        const offer = {
            ...program.data,
            firstMonthBps: invite.data.rates.firstMonthBps,
            recurringBps: invite.data.rates.recurringBps,
            minPayoutCents: invite.data.minPayoutCents,
        }
        body = (
            <>
                <ProgramPitch program={offer} />
                <Card>
                    <CardTitle>Join the SwiftSole affiliate program</CardTitle>
                    <Muted>
                        You've been invited to join at the rates above. This
                        invite expires on {formatDay(invite.data.expiresAt)}
                        {invite.data.emailRestricted
                            ? ' and can only be used with the email address it was sent to'
                            : ''}
                        .
                    </Muted>
                    <Checkbox>
                        <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => setChecked(e.target.checked)}
                        />
                        <span>
                            I agree to the{' '}
                            <a
                                href="/affiliate/terms"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#8fa0ff' }}
                            >
                                affiliate terms
                            </a>{' '}
                            (version {program.data.termsVersion}).
                        </span>
                    </Checkbox>
                    {accept.isError && (
                        <ErrorText>{errorMessage(accept.error)}</ErrorText>
                    )}
                    <div>
                        <ActionButton
                            disabled={!checked || accept.isPending}
                            onClick={submit}
                        >
                            Accept invite
                        </ActionButton>
                    </div>
                </Card>
            </>
        )
    } else {
        body = <ErrorText>{errorMessage(program.error || me.error)}</ErrorText>
    }

    return (
        <Page>
            <Narrow>
                <PageTitle>Affiliate invite</PageTitle>
                {body}
            </Narrow>
        </Page>
    )
}

export default AffiliateInvitePage
